import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom/cjs/react-router-dom.min';
import {VendorLogos} from '~/common/vendors/VendorLogos';
import {ValidationError} from '~/SignUpSheets/components/ValidationError/ValidationError';
import {useUpdateSheet} from '~/SignUpSheets/hooks/useUpdateSheet';
import {selectBlueprintId} from '~/SignUpSheets/reducers/blueprint/selectors';
import {UPDATE_SHEET_DETAILS} from '~/SignUpSheets/reducers/sheet/constants';
import {
  selectErrors,
  selectLocalWishlists,
  selectWishlistName,
  selectWishlistOption,
  selectWishlists,
} from '~/SignUpSheets/reducers/sheet/selectors';
import {isValidURL} from '~/SignUpSheets/utils/validation';
import {Button} from '~library/atoms/Button';
import {Input} from '~library/atoms/Input';
import {Radio} from '~library/atoms/Radio';
import {Typography} from '~library/atoms/Typography';
import {VendorDisc} from '~library/molecules/VendorDisc';
import {ViewWishlists} from './ViewWishlists';

import './Wishlist.sass';

const validateRegistryUrl = (url) => {
  if (url === '') return true;
  return isValidURL(url);
};

const WishlistOption = React.memo(({title, description}) => (
  <div className="wishlist-section__option">
    <Typography variant="paragraph2" semibold>
      {title}
    </Typography>
    <Typography
      className="wishlist-section__option__description"
      variant="paragraph3"
      color="pebble"
    >
      {description}
    </Typography>
  </div>
));

const OptionBody = React.memo(({children, open = false, wishlists, showViewWishlists, error}) => {
  const optionRef = useRef(null);
  const [height, setHeight] = useState('0');
  const [hasTransition, setHasTransition] = useState(true);
  const prevWishlists = useRef(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    if (prevWishlists.current !== wishlists) {
      prevWishlists.current = wishlists;
      setHasTransition(false);
    } else if (error) {
      setHasTransition(false);
    } else {
      setHasTransition(true);
    }

    if (optionRef.current && open) {
      setHeight(`${optionRef.current.offsetHeight}px`);
    } else {
      setHeight('0');
    }
  }, [open, wishlists, showViewWishlists, error]);

  return (
    <div
      style={{height}}
      className={`wishlist-section__option-body ${
        hasTransition ? 'wishlist-section__option-body--transition' : ''
      }`}
    >
      <div ref={optionRef} className="wishlist-section__option-body-content">
        {children}
      </div>
    </div>
  );
});

const LinkList = React.memo(({onChange, wishlists, addWishlist, showAddBtn, error}) => (
  <div className="wishlist-section__link-list">
    {wishlists.map((wishlist, i) => (
      <Input
        data-qa-id={`registry-url-${i + 1}`}
        error={!validateRegistryUrl(wishlist) ? 'Please enter a valid URL.' : null}
        type="text"
        label={i === 0 ? 'link url' : null}
        value={wishlist}
        onChange={(e) => onChange(e, i)}
        placeholder="amazon.com/mywishlist"
        required
        // biome-ignore lint/suspicious/noArrayIndexKey: existing error
        key={i}
      />
    ))}
    <ValidationError error={error} dataQaIdStub="wishlists" />
    {showAddBtn && (
      <Button variant="link" data-qa-id="add-additional-wishlist-button" onClick={addWishlist}>
        + Additional wishlist link
      </Button>
    )}
  </div>
));

export const Wishlist = () => {
  const dispatch = useDispatch();
  const localWishlists = useSelector(selectLocalWishlists, shallowEqual);

  const updateLocalWishlists = (value) =>
    dispatch({
      type: UPDATE_SHEET_DETAILS,
      payload: {localWishlists: value},
    });

  const {updateSheetDetails} = useUpdateSheet();
  const [localWishlistName, setLocalWishlistName] = useState('');
  const [saveBtnDisabled, setSaveBtnDisabled] = useState(true);
  const [showViewWishlists, setShowViewWishlists] = useState(false);
  const [wishlistOption, setWishlistOption] = useState(useSelector(selectWishlistOption));
  const wishlistName = useSelector(selectWishlistName);
  const wishlists = useSelector(selectWishlists, shallowEqual);
  const errors = useSelector(selectErrors, shallowEqual);
  const blueprintId = useSelector(selectBlueprintId);

  const hasNonEmptyWishlists = useMemo(() => wishlists.filter((w) => !!w).length > 0, [wishlists]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    updateLocalWishlists(hasNonEmptyWishlists ? wishlists : ['']);
    if (hasNonEmptyWishlists) {
      setShowViewWishlists(true);
    }
  }, [wishlists, hasNonEmptyWishlists]);

  useEffect(() => {
    setLocalWishlistName(wishlistName);
  }, [wishlistName]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const handleRadioChange = useCallback(
    (value) => {
      setWishlistOption(value);
    },
    [setWishlistOption]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const handleWishlistsChange = useCallback(
    (e, index) => {
      const {value} = e.target;
      const newWishlists = [...localWishlists];
      newWishlists[index] = value;
      updateLocalWishlists(newWishlists);
      updateSheetDetails({payload: {}, errorsToClear: [['wishlists']]}); // Clear wishlists error without saving anything
    },
    [localWishlists]
  );

  const saveWishlist = useCallback(() => {
    const nonEmptyWishlists = localWishlists.filter((wishlist) => wishlist !== '');
    updateSheetDetails({
      payload: {
        // biome-ignore lint/style/useNamingConvention: existing error
        wishlist_name: localWishlistName,
        wishlists: nonEmptyWishlists,
        // biome-ignore lint/style/useNamingConvention: existing error
        has_unsaved_wishlists: nonEmptyWishlists.length > 0 ? undefined : false,
        // biome-ignore lint/style/useNamingConvention: existing error
        wishlist_option: 'my-wishlist',
      },
      errorsToClear: [['wishlists']],
    });
    setShowViewWishlists(true);
  }, [updateSheetDetails, localWishlistName, localWishlists]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const removeWishlist = useCallback(
    (wishlist) => {
      const wishlistsArray = [...wishlists];

      const index = wishlistsArray.indexOf(wishlist);
      if (wishlistsArray.length > 1) {
        wishlistsArray.splice(index, 1);
        updateLocalWishlists(wishlistsArray);
        updateSheetDetails({
          payload: {
            wishlists: wishlistsArray,
          },
        });
      } else {
        setShowViewWishlists(false);
        updateLocalWishlists(['']);
        setSaveBtnDisabled(true);
        updateSheetDetails({
          payload: {
            // biome-ignore lint/style/useNamingConvention: existing error
            wishlist_name: '',
            wishlists: [''],
            // biome-ignore lint/style/useNamingConvention: existing error
            wishlist_option: 'evite-wishlist',
          },
        });
      }
    },
    // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
    [wishlists, updateLocalWishlists, setShowViewWishlists, updateSheetDetails]
  );

  const wishlistsAreValid = (arr) => {
    if (arr.filter((i) => i !== '').length === 0) return false; // All wishlists are empty
    for (let i = 0; i < arr.length; i++) {
      if (!isValidURL(arr[i]) && arr[i] !== '') return false;
    }
    return true;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    setSaveBtnDisabled(!wishlistsAreValid(localWishlists));
  }, [localWishlistName, localWishlists, wishlistName, wishlists]);

  const wishlistError = useMemo(
    () => errors.find((e) => e.loc?.[0] === 'wishlists')?.msg ?? null,
    [errors]
  );

  const location = useLocation();
  const wishlistRef = useRef(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    if (location.hash === '#signup-sheet__wishlist-section' && wishlistRef.current) {
      wishlistRef.current.scrollIntoView({block: 'center'});
    }
  }, [wishlistRef]);

  return (
    <div
      ref={wishlistRef}
      id="signup-sheet__wishlist-section"
      className="signup-sheet__wishlist-section"
    >
      <Radio
        className="wishlist-section__radio"
        label={
          <>
            <WishlistOption
              title="Add wishlist"
              description="Let volunteers know exactly what to bring"
            />
            <VendorLogos />
          </>
        }
        checked={wishlistOption === 'my-wishlist'}
        value="my-wishlist"
        onChange={() => handleRadioChange('my-wishlist')}
        data-qa-id="wishlist-section__option__my-wishlist"
      />
      <OptionBody
        open={wishlistOption === 'my-wishlist'}
        wishlists={localWishlists}
        showViewWishlists={showViewWishlists}
        error={wishlistError}
      >
        {wishlists.length > 0 && showViewWishlists
          ? <ViewWishlists
              wishlists={wishlists}
              wishlistName={wishlistName}
              onEditClick={() => setShowViewWishlists(false)}
              onRemoveClick={(wishlist) => removeWishlist(wishlist)}
              blueprintId={blueprintId}
            />
          : <>
              <div className="wishlist-section__input-wrapper">
                <Input
                  type="text"
                  label="Wishlist Name"
                  placeholder="Teacher's wishlist, class supplies list, etc."
                  value={localWishlistName}
                  data-qa-id="wishlist-name"
                  onChange={(e) => {
                    setLocalWishlistName(e.target.value);
                  }}
                />
                <LinkList
                  onChange={handleWishlistsChange}
                  wishlists={localWishlists}
                  showAddBtn={localWishlists.length < 3}
                  addWishlist={() =>
                    localWishlists.length < 3 && updateLocalWishlists([...localWishlists, ''])
                  }
                  error={wishlistError}
                />
              </div>
              <Button
                className="wishlist-section__save-wishlist-button"
                variant="secondary"
                disabled={saveBtnDisabled}
                data-qa-id="save-wishlist-button"
                size="large"
                fullWidth
                onClick={saveWishlist}
              >
                Save wishlist
              </Button>
            </>}
      </OptionBody>
      <Radio
        className="wishlist-section__radio wishlist-section__radio-last"
        label={
          <WishlistOption
            title="Add supplies"
            description="Help volunteers buy supplies easily with a link to Amazon"
          />
        }
        checked={wishlistOption === 'evite-wishlist'}
        value="evite-wishlist"
        onChange={() => handleRadioChange('evite-wishlist')}
        data-qa-id="wishlist-section__option__evite-wishlist"
      />
      <OptionBody open={wishlistOption === 'evite-wishlist'}>
        <div className="wishlist-section__vendor-list">
          <VendorDisc
            imgSrc="https://g0.evitecdn.com/pages/__gift_registry_vendor_images/5941379294363648/Amazon@2x.png"
            href={`https://www.amazon.com/?tag=sus-tracking-20&asc_source=sus_${blueprintId}%7C%7C%7Cnoreg`}
            displayText="Shop supplies now"
            vendorName="Amazon"
            size="large"
            dataQaId="af-sus-amazon"
          />
        </div>
      </OptionBody>
    </div>
  );
};
